import React, { useEffect, useMemo, useState } from "react";

import {
  acervoDeCartas,
  cartasOrfas,
  ColecaoDeCartas,
  possuidosDoServidor
} from "../../../components/TrilhaLudica";
import URL from "../../../config/api";
import axios from "../../../config/axios";

/**
 * A PONTE entre a página de conquistas e a coleção de cartas da trilha lúdica.
 *
 * Existe pelo mesmo motivo de `components/ItensDaAtividade`: o módulo `TrilhaLudica`
 * é autocontido e nenhum import sai dele, então é aqui que vivem `config/api` e
 * `config/axios`. O módulo entrega o componente e as funções puras; quem sabe qual é
 * a URL e onde está o token é o host.
 *
 * A SEÇÃO SOME QUANDO A ROTA NÃO RESPONDE, e isso é decisão e não descuido. O
 * backend desta feature pode não estar publicado no ambiente (foi exatamente o que
 * aconteceu com `POST .../experience`, e o sintoma foi uma sessão de depuração
 * perdida), e um bloco de erro no meio das medalhas do aluno não lhe serve para
 * nada — ele não pode consertar. Quem precisa saber é quem está testando, e para
 * isso há o `console.warn`: engolir o erro sem rastro é o defeito que a regra de
 * depuração do CLAUDE.md proíbe.
 */
function ColecaoDeCartasDoAluno({ token, onResumo }) {
  /* Três estados e não um: `null` em `possuidos` significa "ainda não sei", que é
     diferente de `{}` ("sei, e ele não tem nenhuma"). Com um só, o aluno sem cartas
     e o aluno cuja resposta não chegou veriam a mesma tela — e a primeira frase da
     seção ("você ainda não descobriu nenhuma") seria mentira na metade dos casos. */
  const [possuidos, setPossuidos] = useState(null);
  const [indisponivel, setIndisponivel] = useState(false);

  /* O acervo é o CATÁLOGO EM CÓDIGO: não muda entre renderizações e não depende de
     nada. `useMemo` sem dependências porque `acervoDeCartas()` monta objetos novos a
     cada chamada, e objeto novo aqui invalida o `useMemo` do cruzamento lá dentro. */
  const acervo = useMemo(() => acervoDeCartas(), []);

  useEffect(() => {
    if (!token) return undefined;

    /* `cancelado` porque a resposta pode chegar depois de o aluno sair da página —
       `setState` em componente desmontado é aviso no console e, pior, sinal de que
       alguém vai tentar "consertar" o aviso em vez do que ele indica. */
    let cancelado = false;

    axios
      .get(`${URL.api}activity/ludic-trail/collection`, {
        headers: { authorization: `Bearer ${token}` }
      })
      .then(resposta => {
        if (cancelado) return;

        const dados = resposta && resposta.data;
        if (!dados || !dados.success || !dados.data) {
          // 200 com `success: false` é como este backend devolve erro de aplicação
          // (ver o catch do `collection`), então não cai no `.catch` daqui.
          // eslint-disable-next-line no-console
          console.warn(
            "Coleção de cartas: o servidor respondeu sem sucesso.",
            dados && dados.error
          );
          setIndisponivel(true);
          return;
        }

        setPossuidos(possuidosDoServidor(dados.data.cards));
      })
      .catch(erro => {
        if (cancelado) return;

        const status = erro && erro.response && erro.response.status;
        // eslint-disable-next-line no-console
        console.warn(
          `Coleção de cartas: não foi possível buscar do servidor${
            status ? ` (HTTP ${status}` : " ("
          }${
            status === 404
              ? " — a rota não existe; o backend desta feature não está publicado"
              : ""
          }).`
        );
        setIndisponivel(true);
      });

    return () => {
      cancelado = true;
    };
  }, [token]);

  /* CARTA QUE O CATÁLOGO NÃO CONHECE MAIS é a única falha silenciosa possível aqui, e
     acontece quando um `card_id` é renomeado depois de a coleção existir — a carta
     desaparece da tela sem erro nenhum, e quem sente é o aluno, que perdeu uma carta.
     Ver o aviso em `conteudo/contrato.js`. */
  useEffect(() => {
    if (!possuidos) return;

    const orfas = cartasOrfas(acervo, possuidos);
    if (orfas.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        `Coleção de cartas: ${orfas.length} carta(s) do aluno não existem no ` +
          `catálogo e não aparecem na tela — ${orfas.join(", ")}. ` +
          "Isso indica `card_id` renomeado depois de a coleção existir."
      );
    }
  }, [acervo, possuidos]);

  if (indisponivel || !token) return null;

  /* `cabecalho={false}` porque nesta página o título e o contador são da MOLDURA da
     seção, igual para todo tipo de conquista (ver `SecaoDeConquista`) — e o `onResumo`
     é como o número chega até lá. O módulo desenha o próprio cabeçalho por padrão, o
     que é o certo para quem o plugar noutro host, onde moldura não existe. */
  return (
    <ColecaoDeCartas
      acervo={acervo}
      possuidos={possuidos || {}}
      carregando={possuidos === null}
      cabecalho={false}
      onResumo={onResumo}
    />
  );
}

export default ColecaoDeCartasDoAluno;
